import axios from 'axios';
import { logError } from '../utils/errorHandlers';

const getAktuellesApiBaseUrl = () => {
    const base = process.env.REACT_APP_GALLERY_API_BASE_URL;
    if (!base) return null;
    return base.endsWith('/') ? base.slice(0, -1) : base;
};

const buildApiUrl = (path) => {
    const base = getAktuellesApiBaseUrl();
    if (!base) throw new Error('Missing REACT_APP_GALLERY_API_BASE_URL');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${normalizedPath}`;
};

const buildHeaders = (token) => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
};

/**
 * Fetches all aktuelles posts with pagination.
 *
 * @param {Object} options
 * @param {number} [options.limit=10]
 * @param {number} [options.page=1]
 * @returns {Promise<{posts: Array, total: number, page: number, limit: number}>}
 */
export const fetchAktuellesPosts = async ({ limit = 10, page = 1 } = {}) => {
    try {
        const url = buildApiUrl(`/api/aktuelles?limit=${limit}&page=${page}`);
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        logError('[aktuellesService] Failed to fetch posts', error);
        throw error;
    }
};

/**
 * Creates a new post.
 *
 * @param {Object} data - { titel, text, datum, fotos }
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export const createAktuellesPost = async (data, token) => {
    const url = buildApiUrl('/api/aktuelles');
    const response = await axios.post(url, data, { headers: buildHeaders(token) });
    return response.data;
};

/**
 * Updates an existing post.
 *
 * @param {string} id - Post ID
 * @param {Object} data - { titel, text, datum, fotos }
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export const updateAktuellesPost = async (id, data, token) => {
    const url = buildApiUrl(`/api/aktuelles/${id}`);
    const response = await axios.patch(url, data, { headers: buildHeaders(token) });
    return response.data;
};

/**
 * Deletes a post.
 *
 * @param {string} id - Post ID
 * @param {string} token - Auth token
 * @returns {Promise<void>}
 */
export const deleteAktuellesPost = async (id, token) => {
    const url = buildApiUrl(`/api/aktuelles/${id}`);
    await axios.delete(url, { headers: buildHeaders(token) });
};

/**
 * Generates presigned upload URLs for post photos.
 *
 * @param {string} postId - Post ID
 * @param {Array<{fileName: string, contentType: string}>} photos
 * @param {string} token - Auth token
 * @returns {Promise<{uploadUrls: Array<{key: string, uploadUrl: string}>}>}
 */
export const uploadAktuellesPhotos = async (postId, photos, token) => {
    const url = buildApiUrl(`/api/aktuelles/${postId}/fotos`);
    const response = await axios.post(url, { photos }, { headers: buildHeaders(token) });
    return response.data;
};
